import * as turf from '@turf/turf'
import { ref, computed } from 'vue'
import store from '@/store/index.js'
import { CLUSTER_RADIUS } from '@/constants'

// pixel <=> 距離計算要定数
const EARTH_RADIUS = 6378137 // 地球の半径 (メートル)
const TILE_SIZE = 256 // タイルサイズ

// イベントコールバック遅延実行用タイマー
let hideTimer = null

/**
 * クラスターのポップアップ表示制御
 */
export function useClusterPopup() {
  /**
   * 表示フラグ
   * @type {boolean}
   */
  const show = ref(false)

  /**
   * エレメントID
   * @type {string}
   */
  const id = ref(null)

  /**
   * 表示オフセット
   * @type {Array.<number>}
   */
  const offset = ref([54, 0])

  /**
   * 表示方向
   * @type {string}
   */
  const placement = ref('right')

  /**
   * クラスタ組成情報
   * @type {Array.<Object>}
   */
  const clusters = ref([])

  /**
   * watcher解除メソッド
   * @type {function}
   */
  const unwatch = ref(null)

  /**
   * ポップアップの状態
   * @type {Object}
   */
  const popup = ref({ show, id, offset, placement, unwatch })

  /**
   * クラスタアイコンにホバー中か判定するフラグ
   * @type {Boolean}
   */
  const isClusterHoverd = ref(false)

  /**
   * ポップアップにホバー中か判定するフラグ
   * @type {Boolean}
   */
  const isPopupHoverd = ref(false)

  /**
   * ホバー中クラスタ
   * @type {Object}
   */
  const hoveredCluster = ref(null)

  /**
   * 現在のズームレベル
   * @type {number}
   */
  const zoom = computed(() => store.state.core.map.zoom)

  /**
   * feature はクラスターか？
   * @param {Object} feature 対象feature
   * @returns {boolean} クラスターの場合はtrue
   */
  const isCluster = (feature) =>
    feature && feature.properties && feature.properties.cluster

  /**
   * クラスタークリック時処理
   * @param {*} object 対象クラスタ
   */
  const onClickCluster = async (object) => {
    // 対象クラスタの固定表示フラグをトグル
    const index = clusters.value.findIndex(
      (cluster) => cluster.id === object.id
    )
    if (index >= 0) {
      clusters.value[index].fixPopup = !clusters.value[index].fixPopup
    }
  }

  /**
   * クラスターホバー時処理
   * @param {Object|undefined} object クラスタ情報 ホバーが外れた際は undefined
   */
  const onHoverCluster = async (object) => {
    // 対象クラスタがすでにポップアップ表示中なら何もせず終了
    const target = clusters.value.find((cluster) =>
      areClustersEqual(cluster, object)
    )
    if (target?.showPopup) return

    // クラスタアイコンにホバー中 => 対象クラスタのみポップアップ表示
    hoveredCluster.value = object
    clusters.value = clusters.value.map((cluster) => ({
      ...cluster,
      showPopup: cluster.id === hoveredCluster.value.id,
    }))
  }

  /**
   * ポップアップmouseenter時処理
   */
  const onPopupMouseEnter = () => {
    isPopupHoverd.value = true
    // 遅延実行用タイマークリア
    clearTimeout(hideTimer)
  }

  /**
   * ポップアップmouseleave時処理
   */
  const onPopupMouseLeave = () => {
    isPopupHoverd.value = false
    /**
     * ポップアップ => アイコンへのマウス移動時、mouseleave が deck.gl レイヤーの
     * onHover より先に発生してポップアップが一瞬ちらつくので遅延実行
     */
    hideTimer = setTimeout(() => {
      // クラスタアイコンにホバーしている場合は表示を継続
      if (isClusterHoverd.value) return

      const index = clusters.value.findIndex(
        (cluster) => cluster.id === hoveredCluster.value.id
      )
      if (index >= 0) clusters.value[index].showPopup = false
    }, 100)
  }

  /**
   * クラスターオブジェクトが同じか判定する
   * クラスタ組成情報の更新で同じクラスタでもIDが変わるため同じクラスタ半径内の座標か判定
   * @param {Object} clusterA - クラスターオブジェクトA
   * @param {Object} clusterB - クラスターオブジェクトB
   * @returns {boolean} - 同じクラスターである場合はtrue、それ以外はfalse
   */
  const areClustersEqual = (clusterA, clusterB) => {
    if (!clusterA || !clusterB) return false

    const distance = turf.distance(
      turf.point(clusterA.geometry.coordinates),
      turf.point(clusterB.geometry.coordinates),
      { units: 'meters' }
    )
    // ズームレベルに応じた1ピクセルの距離（メートル）を計算
    const scale = 2 ** zoom.value
    const metersPerPixel = (2 * Math.PI * EARTH_RADIUS) / (TILE_SIZE * scale)
    // ピクセル距離をメートル単位に変換
    const maxDistanceMeters = CLUSTER_RADIUS * metersPerPixel
    // 同じクラスタ半径内にある場合は同一クラスタと判定
    return distance <= maxDistanceMeters
  }

  /**
   * 現在のクラスタ組成データからポップアップ固定表示フラグを取得
   * @param {Object} data 対象クラスタデータ
   */
  const getFixPopupFromCurrentClusterInfo = (data) => {
    const target = clusters.value.find((cluster) =>
      areClustersEqual(cluster, data)
    )
    return target ? target.fixPopup : false
  }

  return {
    popup,
    isClusterHoverd,
    isPopupHoverd,
    clusters,
    hoveredCluster,
    isCluster,
    onClickCluster,
    onHoverCluster,
    onPopupMouseEnter,
    onPopupMouseLeave,
    areClustersEqual,
    getFixPopupFromCurrentClusterInfo,
  }
}
